import { hostAppApi } from '@/plugins/axios_settings'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import lgFullscreen from 'lightgallery/plugins/fullscreen'
import lgRotate from 'lightgallery/plugins/rotate'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-thumbnail.css'
import 'lightgallery/css/lg-zoom.css'
import 'lightgallery/css/lg-fullscreen.css'
import 'lightgallery/css/lg-rotate.css'

/*
 * Shared document preview gallery (lightGallery) for module list views.
 *
 * A view using this mixin must:
 *   - add `<div ref="lgContainer" style="display:none"></div>` to its template
 *   - expose `getHostRefApi` (mapped getter) and a `hostUserId` data property
 *   - add to a list-item column config:
 *       prefixIcon: 'mdi-image-multiple',
 *       prefixIconSize: 32,
 *       prefixIconClick: this.previewDocuments,
 *       prefixIconCondition: this.hasDocuments
 *   - implement `documentEditUrl(item)` returning the module's `/{id}/edit` URL,
 *     whose response contains a `documents` array (same shape as the Documents component).
 *
 * The row's preview icon only shows when the list payload marks the row as having
 * documents via `documents_count` / `has_documents` / a `documents` array.
 */
export default {
  data () {
    return {
      lgInstance: null
    }
  },
  methods: {
    hasDocuments (item) {
      // Always show the preview icon; documents are resolved lazily when the
      // icon is clicked (see previewDocuments). This avoids an N+1 probe of the
      // per-row `/{id}/edit` endpoint on list load.
      return !!item
    },
    buildGalleryItems (docs) {
      const imageExt = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp']
      return docs
        .filter(d => d.downloadUrl && (imageExt.includes(d.extention) || d.extention === 'pdf'))
        .map(d => {
          const title = d.title || d.original_name || ''
          if (imageExt.includes(d.extention)) {
            return { src: d.downloadUrl, thumb: d.downloadUrl, subHtml: `<h4>${title}</h4>` }
          }
          const pdfThumb = 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="#2b2b2b"/><rect x="18" y="10" width="44" height="60" rx="3" fill="#e53935"/><text x="40" y="48" font-family="Arial,Helvetica,sans-serif" font-size="16" font-weight="bold" fill="#fff" text-anchor="middle">PDF</text></svg>')
          // Render the PDF straight in the iframe. Google's embedded viewer was used
          // here before, but it needs a publicly reachable URL — our downloadUrl is
          // host/auth-scoped, so Google's fetch fails and the slide shows
          // "No preview available".
          return { src: d.downloadUrl, iframe: true, thumb: pdfThumb, subHtml: `<h4>${title}</h4>` }
        })
    },
    previewDocuments (item) {
      const url = this.documentEditUrl(item)
      if (!url) return
      // Most modules return documents under `documents`; a few use a custom key
      // (e.g. weldlog_documents, materiallist_documents) which the view exposes via a
      // `documentsKey` data/computed value or method.
      const docsKey = (typeof this.documentsKey === 'function' ? this.documentsKey() : this.documentsKey) || 'documents'
      hostAppApi.get(url)
        .then(response => {
          const raw = response.data ? response.data[docsKey] : null
          const docs = raw ? this.$formatter.removeInvalidFromArrayObject(raw) : []
          const galleryItems = docs.length ? this.buildGalleryItems(docs) : []
          if (!galleryItems.length) {
            this.$root.$emit('snackbar', { snackbar: true, color: 'warning', text: 'message.customModule.urlNotFound' })
            return
          }
          this.openGallery(galleryItems)
        })
    },
    openGallery (items, startIndex = 0) {
      if (this.lgInstance) {
        this.lgInstance.destroy()
        this.lgInstance = null
      }
      this.lgInstance = lightGallery(this.$refs.lgContainer, {
        dynamic: true,
        dynamicEl: items,
        plugins: [lgThumbnail, lgZoom, lgFullscreen, lgRotate],
        thumbnail: true,
        download: true,
        closable: true,
        // We handle Escape ourselves (see below) so it can't bubble up to a
        // surrounding Vuetify v-dialog and close that too.
        escKey: false,
        zoom: true,
        scale: 1,
        zoomFromOrigin: false,
        showZoomInOutIcons: true,
        actualSize: false,
        rotate: true,
        rotateLeft: true,
        rotateRight: true,
        mobileSettings: {
          controls: false,
          showCloseIcon: true,
          download: false
        }
      })
      this.bindGalleryEscHandler()
      this.lgInstance.openGallery(startIndex)
    },
    // When the gallery is opened from inside a v-dialog (e.g. the weld log form),
    // a single Escape press would otherwise close both the gallery and the dialog,
    // dropping the user back to the list. Handle Escape in the capture phase, close
    // only the gallery, and stop the event before it reaches the dialog's listener.
    bindGalleryEscHandler () {
      this.removeGalleryEscHandler()
      this._galleryEscHandler = (e) => {
        if (e.keyCode !== 27 && e.key !== 'Escape') return
        if (!this.lgInstance || !this.lgInstance.lgOpened) return
        e.stopPropagation()
        e.preventDefault()
        this.lgInstance.closeGallery()
      }
      window.addEventListener('keydown', this._galleryEscHandler, true)
    },
    removeGalleryEscHandler () {
      if (this._galleryEscHandler) {
        window.removeEventListener('keydown', this._galleryEscHandler, true)
        this._galleryEscHandler = null
      }
    }
  },
  beforeDestroy () {
    this.removeGalleryEscHandler()
    if (this.lgInstance) {
      this.lgInstance.destroy()
      this.lgInstance = null
    }
  }
}
